import { Text, designTokens } from '@cinch-labs/design-system'
import cx from 'classnames'
import React from 'react'

import { useCarsCarouselTracking } from '../../../carousel-section/carousel-with-controls/carousel-with-controls'
import { Card } from '../../../shared/card/card'
import { Price } from '../price/price'
import { ProductImage } from '../product-image/product-image'
import { SaveButton } from '../save-button/save-button'
import { Subtitle } from '../subtitle/subtitle'
import { Tags } from '../tags/tags'
import { Title } from '../title/title'
import styles from './product-list-card.module.css'
import { useUserStore } from '@cinch-nx/data-user'

interface TagItem {
  label: string
  value: string
}

export interface ProductListCardProps {
  id: string
  image?: string
  price: number
  priceIncludingAdminFee: number
  title: string
  subtitle: string
  isAvailable?: boolean
  isSaved: boolean
  isPending: boolean
  tags: TagItem[]
  unavailable?: string
  isUnfavourited?: boolean
  isReserved: boolean
  quoteRegularPaymentInPence?: number | null
  quoteType?: string | null
  make: string
  model: string
  vehicleYear?: string | number
  onSave?: (newVal: boolean) => void
  discountInPence: number
}

const filterTestTags = (tag: TagItem, isNewCar: boolean) =>
  tag.label && isNewCar
    ? tag.label.match(/Fuel Type|Transmission Type|Condition/)
    : tag.label.match(/Mileage|Fuel Type|Transmission Type/)

const hyphenate = (text: string | undefined) =>
  text ? text.replace(/\s+/g, '-').toLowerCase() : ''

const { font } = designTokens.customProperties.ds
const ERROR_MESSAGE = 'Image Unavailable'
const penceToPounds = (n: number): string => `${Math.round(n / 100).toFixed(0)}`

export const ProductListCard: React.FC<ProductListCardProps> = ({
  id,
  price,
  priceIncludingAdminFee,
  quoteRegularPaymentInPence,
  image,
  title,
  subtitle,
  isSaved,
  isPending = false,
  isAvailable,
  onSave,
  tags,
  unavailable,
  isReserved,
  quoteType,
  make,
  model,
  vehicleYear,
  discountInPence,
}) => {
  const isLoggedIn = useUserStore((state) => state.isLoggedIn())

  const isNewCar = tags.some((tag) => tag.value === 'NEW CAR')

  const tagsCarCards = tags.filter((tag) => filterTestTags(tag, isNewCar))
  const vehicleTitle = `${vehicleYear ?? ''} ${title}`

  const carsCarouselTrackingEvents = useCarsCarouselTracking()
  const shouldShowCarsOnOfferTag = discountInPence > 0

  const urlPrefix = isNewCar ? '/new-car' : '/used-cars'

  return (
    <Card data-testid="product-list-card" element="article" isHoverable={true}>
      <div
        className={cx(styles.meta, styles.metaCarCards, {
          [styles.unavailable]: !isAvailable,
        })}
      >
        <Title
          element="h2"
          fontSizeFixed={'m'}
          type={'cardTitle'}
          isPending={isPending}
          aria-describedby={isReserved ? `vehicle-reserved-${id}` : undefined}
        >
          <a
            data-testid="product-list-card-link"
            href={`${urlPrefix}/${hyphenate(make)}/${hyphenate(
              model,
            )}/details/${id}`}
            className={styles.link}
            onClick={() => carsCarouselTrackingEvents.onVehicleClick()}
          >
            {vehicleTitle}
          </a>
        </Title>

        <Subtitle isPending={isPending} fontSize={'s'}>
          {subtitle}
        </Subtitle>

        <Tags
          isPending={isPending}
          listStyleClassName={styles.tags}
          listItemClassName={styles.tagsItem}
          items={tagsCarCards}
        />

        {isAvailable && !!priceIncludingAdminFee && !!price && (
          <Price
            isPending={isPending}
            price={isNewCar ? price : priceIncludingAdminFee}
            quoteRegularPaymentInPence={quoteRegularPaymentInPence}
            quoteType={quoteType}
            discountInPence={discountInPence}
            shouldShowCarsOnOfferTag={shouldShowCarsOnOfferTag}
          />
        )}
      </div>

      <div className={styles.imageContainer}>
        <ProductImage
          alt={title}
          isAvailable={isAvailable}
          image={image}
          unavailableImageMessage={
            (!isAvailable && ' ') || unavailable || ERROR_MESSAGE
          }
          isPending={isPending}
        />

        <div className={styles.actions}>
          {isLoggedIn && !!onSave && isAvailable && (
            <SaveButton
              isPending={isPending || (!isAvailable && !isSaved)}
              className={styles.save}
              isSaved={isSaved}
              onClick={() => onSave(!isSaved)}
              size={font.size.xxl}
            />
          )}
        </div>

        {shouldShowCarsOnOfferTag && !isReserved && (
          <Text
            fontSizeFixed="s"
            fontWeight="semibold"
            className={`${styles.badge} ${styles.discount}`}
            data-testid="vehicle-has-discount"
            id={`vehicle-discount-${id}`}
          >
            {`£${penceToPounds(discountInPence ?? 0)} off`}
          </Text>
        )}

        {isReserved && (
          <Text
            fontWeight="book"
            className={`${styles.badge} ${styles.reserved}`}
            data-testid="vehicle-is-reserved"
            id={`vehicle-reserved-${id}`}
          >
            Currently reserved
          </Text>
        )}
      </div>
    </Card>
  )
}
